import React, { useState, useEffect, useRef } from 'react';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function ApgarCalculator({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [appearance, setAppearance] = useState('2');
  const [pulse, setPulse] = useState('2');
  const [grimace, setGrimace] = useState('2');
  const [activity, setActivity] = useState('2');
  const [respiration, setRespiration] = useState('2');
  const [apgarScore, setApgarScore] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const intervalRef = useRef(null);
  const flashIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
    };
  }, []);

  const handleCalculate = () => {
    const app = parseInt(appearance);
    const pul = parseInt(pulse);
    const gri = parseInt(grimace);
    const act = parseInt(activity);
    const res = parseInt(respiration);
    const total = app + pul + gri + act + res;

    setApgarScore(total);
    startTimer(4 * 60); // 4 minutes in seconds
  };

  const startTimer = (durationInSeconds) => {
    // Clear any existing timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);

    setTimerActive(true);
    setTimeRemaining(durationInSeconds);
    setIsFlashing(false);

    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;

        // Start flashing when < 60 seconds
        if (newTime < 60 && newTime >= 0) {
          if (!flashIntervalRef.current) {
            setIsFlashing(true);
            flashIntervalRef.current = setInterval(() => {
              setIsFlashing((flash) => !flash);
            }, 500);
          }
        }

        // Timer complete
        if (newTime < 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
          setIsFlashing(false);
          setTimerActive(false);
          return 0;
        }

        return newTime;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="container-center" style={{ paddingBottom: '80px' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="card card-center" style={{
        maxWidth: '700px',
        width: '100%',
        padding: '50px 40px'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          borderBottom: '2px solid #0E3293',
          paddingBottom: '25px'
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#0E3293',
            margin: '0 0 15px 0',
            letterSpacing: '-0.5px'
          }}>
            APGAR Score Calculator
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#777',
            margin: '0',
            lineHeight: '1.6'
          }}>
            Select a score for each category
          </p>
        </div>

        {/* APGAR Form */}
        <div style={{
          background: '#f8f9fa',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Appearance */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '15px'
            }}>
              Appearance (Skin Color):
            </label>
            <select
              value={appearance}
              onChange={(e) => setAppearance(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="0">0 - Blue or Pale All Over</option>
              <option value="1">1 - Pink Body, Blue Extremities</option>
              <option value="2">2 - Completely Pink</option>
            </select>
          </div>

          {/* Pulse */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '15px'
            }}>
              Pulse (Heart Rate):
            </label>
            <select
              value={pulse}
              onChange={(e) => setPulse(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="0">0 - Absent</option>
              <option value="1">1 - Less than 100 bpm</option>
              <option value="2">2 - At Least 100 bpm</option>
            </select>
          </div>

          {/* Grimace */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '15px'
            }}>
              Grimace (Reflex Irritability):
            </label>
            <select
              value={grimace}
              onChange={(e) => setGrimace(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="0">0 - No Response</option>
              <option value="1">1 - Grimace</option>
              <option value="2">2 - Cry or Pull Away</option>
            </select>
          </div>

          {/* Activity */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '15px'
            }}>
              Activity (Muscle Tone):
            </label>
            <select
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="0">0 - None</option>
              <option value="1">1 - Some Flexion</option>
              <option value="2">2 - Active Movement</option>
            </select>
          </div>

          {/* Respiration */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '15px'
            }}>
              Respiration (Breathing Effort):
            </label>
            <select
              value={respiration}
              onChange={(e) => setRespiration(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="0">0 - Absent</option>
              <option value="1">1 - Weak, Irregular</option>
              <option value="2">2 - Strong Cry</option>
            </select>
          </div>

          {/* Calculate Button */}
          <button
            onClick={handleCalculate}
            className="btn btn-primary btn-full"
            style={{
              background: '#0E3293',
              borderColor: '#0E3293',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Calculate APGAR Score
          </button>
        </div>

        {/* APGAR Score Display */}
        {apgarScore !== null && (
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            border: '2px solid #e0e0e0',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: '0 0 10px 0',
              fontSize: '15px',
              color: '#666',
              fontWeight: '500'
            }}>
              APGAR Score:
            </p>
            <span style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#0E3293'
            }}>
              {apgarScore}
            </span>
          </div>
        )}

        {/* Timer Display */}
        {timerActive && (
          <div>
            <div style={{
              background: isFlashing ? '#c62828' : '#0E3293',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              marginBottom: '15px',
              transition: 'background-color 0.3s ease'
            }}>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                color: 'white',
                fontWeight: '500',
                opacity: 0.9
              }}>
                Timer:
              </p>
              <span style={{
                fontSize: '36px',
                fontWeight: 'bold',
                color: 'white'
              }}>
                {formatTime(timeRemaining)}
              </span>
            </div>

            <p style={{
              textAlign: 'center',
              fontSize: '13px',
              color: '#666',
              fontStyle: 'italic',
              margin: '0',
              lineHeight: '1.5'
            }}>
              This timer reminds you to document the APGAR score at 1 minute and 5 minutes after birth.
            </p>
          </div>
        )}

        {!timerActive && timeRemaining === 0 && apgarScore !== null && (
          <div style={{
            background: '#2e7d32',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            marginBottom: '15px'
          }}>
            <p style={{
              margin: '0',
              fontSize: '16px',
              color: 'white',
              fontWeight: '600'
            }}>
              Timer Complete! Document APGAR Score.
            </p>
          </div>
        )}

        {apgarScore === null && (
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            border: '2px solid #e0e0e0',
            textAlign: 'center'
          }}>
            <p style={{
              margin: '0',
              fontSize: '15px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              Select values and click Calculate to see results
            </p>
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <footer style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        background: '#ffffff',
        textAlign: 'center',
        padding: '15px 10px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
        zIndex: 9999,
        borderTop: '1px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <img
            src="https://us.api.fliplet.com/v1/media/files/5174683/contents/blob"
            alt="NarcoticTrack Logo"
            style={{
              height: '32px',
              verticalAlign: 'middle'
            }}
          />
          <span style={{
            fontSize: '14px',
            color: '#666',
            fontWeight: '400'
          }}>
            © 2025 NarcoticTrack Inventory System, LLC
          </span>
        </div>
      </footer>
    </div>
  );
}

export default ApgarCalculator;
